import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { JsonSchema } from '../utils/jsonSchema';

const TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, { schema: JsonSchema; at: number }>();
const inflight = new Map<string, Promise<JsonSchema | null>>();

const cached = (objectId: string): JsonSchema | null => {
  const entry = cache.get(objectId);
  if (!entry) return null;
  if (Date.now() - entry.at > TTL_MS) {
    cache.delete(objectId);
    return null;
  }
  return entry.schema;
};

/**
 * The JSON Schema the backend sends to the model for a custom object (references expanded),
 * from GET /custom-objects/{id}/schema?format=langchain. Resolves to null when it can't be
 * loaded so callers can fall back to a schema built from the object's fields.
 */
export function fetchObjectSchema(
  objectId: string,
  getIdToken: () => Promise<string | null>,
): Promise<JsonSchema | null> {
  const hit = cached(objectId);
  if (hit) return Promise.resolve(hit);
  const pending = inflight.get(objectId);
  if (pending) return pending;

  const request = (async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(
        `/api/v2/custom-objects/${encodeURIComponent(objectId)}/schema?format=langchain`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!response.ok) return null;
      const body = await response.json();
      const schema = body?.data ?? body;
      if (!schema || typeof schema !== 'object' || schema.type !== 'object') return null;
      cache.set(objectId, { schema, at: Date.now() });
      return schema as JsonSchema;
    } catch {
      return null;
    } finally {
      inflight.delete(objectId);
    }
  })();
  inflight.set(objectId, request);
  return request;
}

export function invalidateObjectSchema(objectId?: string): void {
  if (objectId) cache.delete(objectId);
  else cache.clear();
}

/** Runtime schemas for several objects, keyed by id; ids that fail to load are left out. */
export function useObjectSchemas(objectIds: string[], enabled = true): Record<string, JsonSchema> {
  const { getIdToken } = useAuth();
  const key = enabled ? Array.from(new Set(objectIds.filter(Boolean))).sort().join(',') : '';
  const [loaded, setLoaded] = useState<{ key: string; schemas: Record<string, JsonSchema> }>({ key: '', schemas: {} });

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const ids = key.split(',');
    Promise.all(ids.map((id) => fetchObjectSchema(id, getIdToken).then((schema) => [id, schema] as const))).then(
      (entries) => {
        if (cancelled) return;
        const schemas: Record<string, JsonSchema> = {};
        entries.forEach(([id, schema]) => {
          if (schema) schemas[id] = schema;
        });
        setLoaded({ key, schemas });
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return loaded.key === key ? loaded.schemas : {};
}

export function useObjectSchema(
  objectId: string | null | undefined,
  enabled = true,
): { schema: JsonSchema | null; loading: boolean; failed: boolean } {
  const { getIdToken } = useAuth();
  const active = enabled && !!objectId;
  const [state, setState] = useState<{ id: string | null; schema: JsonSchema | null; failed: boolean }>(() => ({
    id: active ? objectId! : null,
    schema: active ? cached(objectId!) : null,
    failed: false,
  }));

  useEffect(() => {
    if (!active || !objectId) return;
    let cancelled = false;
    const hit = cached(objectId);
    if (hit) {
      setState({ id: objectId, schema: hit, failed: false });
      return;
    }
    setState({ id: objectId, schema: null, failed: false });
    fetchObjectSchema(objectId, getIdToken).then((schema) => {
      if (!cancelled) setState({ id: objectId, schema, failed: !schema });
    });
    return () => {
      cancelled = true;
    };
    // getIdToken is recreated on auth state changes; the schema doesn't depend on which token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectId, active]);

  if (!active) return { schema: null, loading: false, failed: false };
  const current = state.id === objectId;
  return {
    schema: current ? state.schema : null,
    loading: !current || (!state.schema && !state.failed),
    failed: current && state.failed,
  };
}
